import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import { EmojiEvents, Save } from '@mui/icons-material';
import { prizeService } from '../services/prizeService';
import { campaignService } from '../services/campaignService';
import { Campaign } from '../types';
import { AppHeader } from '../components/AppHeader';

interface PrizeFormData {
  name: string;
  description?: string;
  quantity: number;
  campaignId: string;
}

const prizeSchema: yup.ObjectSchema<PrizeFormData> = yup.object({
  name: yup.string().required('Nome do prêmio é obrigatório'),
  description: yup.string().optional(),
  quantity: yup.number().required('Quantidade é obrigatória').min(1, 'Quantidade deve ser maior que zero'),
  campaignId: yup.string().required('Campanha é obrigatória')
});

  const PrizeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const isEdit = !!id;

  const { control, handleSubmit, reset, formState: { errors } } = useForm<PrizeFormData>({
    resolver: yupResolver(prizeSchema),
    defaultValues: {
      name: '',
      description: '',
      quantity: 1,
      campaignId: ''
    }
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const data = await campaignService.getCampaignsByAssociation();
      setCampaigns(data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    }
  }, []);

  const fetchPrize = useCallback(async (prizeId: string) => {
    try {
      const prize = await prizeService.getPrizeById(prizeId);
      reset({
        name: prize.name,
        quantity: prize.quantity,
        campaignId: prize.campaign?.id || '',
      });
    } catch (error) {
      console.error('Erro ao carregar prêmio:', error);
      setError('Erro ao carregar dados do prêmio');
    }
  }, [reset]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    if (isEdit && id) {
      fetchPrize(id);
    }
  }, [isEdit, id, fetchPrize]);

  const onSubmit: SubmitHandler<PrizeFormData> = async (data) => {
    try {
      setLoading(true);
      setError('');

      const prizeData = {
        name: data.name,
        description: data.description || undefined,
        quantity: data.quantity,
        campaignId: data.campaignId
      };

      if (isEdit && id) {
        await prizeService.updatePrize(id, prizeData);
      } else {
        await prizeService.createPrize(prizeData);
      }

      navigate('/prizes');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar prêmio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppHeader 
        title={isEdit ? 'Editar Prêmio' : 'Novo Prêmio'}
        subtitle={isEdit ? 'Atualize as informações do prêmio' : 'Cadastre um novo prêmio para suas campanhas'}
        icon={<EmojiEvents />}
        backTo="/prizes"
      />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, elevation: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {isEdit ? 'Editar Prêmio' : 'Novo Prêmio'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Informações Básicas */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Informações do Prêmio
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome do Prêmio"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Descrição (opcional)"
                    multiline
                    rows={3}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              />

              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Quantidade"
                    type="number"
                    inputProps={{ min: 1 }}
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              />

              <Controller
                name="campaignId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.campaignId}>
                    <InputLabel>Campanha</InputLabel>
                    <Select
                      {...field}
                      label="Campanha"
                      sx={{ borderRadius: 2 }}
                    >
                      {campaigns.map((campaign) => (
                        <MenuItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.campaignId && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.campaignId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              {/* Botões de Ação */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/prizes')}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  {loading ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar Prêmio')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </>
  );
}; 
export default PrizeForm;